/**
 * gagoz-collection.js
 *
 * <gagoz-collection> — Infinite marquee slider.
 *
 *   - requestAnimationFrame loop: continuously scrolls left at constant speed
 *   - All items cloned once for a seamless infinite loop
 *   - Pauses on mouseenter, resumes on mouseleave
 *   - Pointer + touch drag to scrub forward or backward
 *   - Touch: only activates on primarily horizontal movement
 *     (preserves native vertical page scroll)
 *   - Re-measures loop width on window resize
 */

class GagozCollection extends HTMLElement {
  connectedCallback() {
    const speedSetting = parseFloat(this.dataset.marqueeSpeed) || 3;
    // px per ms: scale 1 → 0.035 px/ms (very slow), 10 → 0.35 px/ms (fast)
    this.speed = speedSetting * 0.035;
    this.autoplayOn = this.dataset.autoplay !== "false";

    this._offset = 0;
    this._loopWidth = 0;
    this._running = false;
    this.isPaused = false;
    this.isDragging = false;
    this._rafId = null;
    this._lastTs = null;
    this._isHovered = false;
    this._resumeTimer = null;

    this._track = this.querySelector(".gagoz-collection__track");
    this._viewport = this.querySelector(".gagoz-collection__viewport");

    if (!this._track || !this._viewport) return;

    this._mql = window.matchMedia("(max-width: 749px)");
    this._onMql = () => this._syncMode();
    if (this._mql.addEventListener) this._mql.addEventListener("change", this._onMql);
    else this._mql.addListener(this._onMql);
    this._syncMode();

    if (this.autoplayOn) {
      this.addEventListener("mouseenter", () => {
        this._isHovered = true;
        clearTimeout(this._resumeTimer);
        this._pause();
      });
      this.addEventListener("mouseleave", () => {
        this._isHovered = false;
        clearTimeout(this._resumeTimer);
        this._resume();
      });
    }

    this._setupDrag();

    this._onResize = () => this._measureLoop();
    window.addEventListener("resize", this._onResize, { passive: true });
  }

  disconnectedCallback() {
    this._running = false;
    if (this._rafId) cancelAnimationFrame(this._rafId);
    clearTimeout(this._resumeTimer);
    window.removeEventListener("resize", this._onResize);
    if (this._mql) {
      if (this._mql.removeEventListener) this._mql.removeEventListener("change", this._onMql);
      else this._mql.removeListener(this._onMql);
    }
  }

  _isMobile() {
    return this._mql ? this._mql.matches : window.matchMedia("(max-width: 749px)").matches;
  }

  _syncMode() {
    if (this._isMobile()) {
      this._running = false;
      if (this._rafId) cancelAnimationFrame(this._rafId);
      this._rafId = null;
      if (this._track) this._track.style.transform = "";
      this.isPaused = true;
      return;
    }
    if (!this._loopWidth) this._setupMarquee();
    else {
      this.isPaused = false;
      if (!this._running) this._startRaf();
    }
  }

  // ── Setup ─────────────────────────────────────────────────────────────

  _setupMarquee() {
    if (this._setupDone) return;
    this._setupDone = true;
    const originals = [
      ...this._track.querySelectorAll(".gagoz-collection__item"),
    ];
    this._originalCount = originals.length;

    if (this._originalCount === 0) return;

    // Clone all items and append — enables seamless wrap-around
    originals.forEach((item) => {
      const clone = item.cloneNode(true);
      clone.setAttribute("aria-hidden", "true");
      this._track.appendChild(clone);
    });

    // Two frames: first lets the browser flush styles, second measures layout
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._measureLoop();
        this._startRaf();
      });
    });
  }

  _measureLoop() {
    const first = this._track.querySelector(".gagoz-collection__item");
    if (!first) return;
    const gapPx = parseFloat(getComputedStyle(this._track).columnGap) || 16;
    this._loopWidth = this._originalCount * (first.offsetWidth + gapPx);
  }

  // ── Animation loop ────────────────────────────────────────────────────

  _startRaf() {
    this._running = true;
    const loop = (ts) => {
      if (!this._running) return;
      if (this._lastTs !== null && !this.isPaused && !this.isDragging) {
        const delta = ts - this._lastTs;
        this._offset -= this.speed * delta;
        this._normalizeOffset();
        this._applyTransform();
      }
      this._lastTs = ts;
      this._rafId = requestAnimationFrame(loop);
    };
    this._rafId = requestAnimationFrame(loop);
  }

  _normalizeOffset() {
    if (!this._loopWidth) return;
    while (this._offset <= -this._loopWidth) this._offset += this._loopWidth;
    while (this._offset > 0) this._offset -= this._loopWidth;
  }

  _applyTransform() {
    this._track.style.transform = `translateX(${this._offset}px)`;
  }

  _pause() {
    this.isPaused = true;
  }
  _resume() {
    this.isPaused = false;
    this._lastTs = null;
  }

  // ── Drag / touch ──────────────────────────────────────────────────────

  _setupDrag() {
    let startX = 0;
    let startY = 0;
    let dirLocked = false;
    let isHorizontal = false;
    let pointerIsDown = false;

    const onDown = (e) => {
      if (this._isMobile()) return;
      pointerIsDown = true;
      this.isDragging = false;
      this._dragStartX = e.clientX;
      this._dragStartOff = this._offset;
      startX = e.clientX;
      startY = e.clientY;
      dirLocked = false;
      isHorizontal = false;
      clearTimeout(this._resumeTimer);
      this._pause();
    };

    const onMove = (e) => {
      if (!pointerIsDown) return;

      // Mouse + touch: yönü ilk anlamlı harekette belirle
      if (!dirLocked) {
        const dx = Math.abs(e.clientX - startX);
        const dy = Math.abs(e.clientY - startY);
        if (dx < 5 && dy < 5) return; // yeterli hareket yok
        dirLocked = true;
        isHorizontal = dx >= dy;
        if (isHorizontal) {
          // Yatay drag onaylandı — pointer'i şimdi yakala
          this.isDragging = true;
          this._viewport.setPointerCapture(e.pointerId);
          this._viewport.classList.add("is-dragging");
        } else {
          // Dikey — iptal, native scroll devreye girsin
          pointerIsDown = false;
          this._resume();
          return;
        }
      }

      if (!this.isDragging) return;

      this._offset = this._dragStartOff + (e.clientX - this._dragStartX);
      this._normalizeOffset();
      this._applyTransform();
    };

    const onEnd = (e) => {
      if (!pointerIsDown) return;
      pointerIsDown = false;
      if (this.isDragging) {
        this.isDragging = false;
        this._viewport.classList.remove("is-dragging");
      }

      if (e && e.pointerType === "touch") {
        // Mobil: 3 sn bekle, sonra oyna
        clearTimeout(this._resumeTimer);
        this._resumeTimer = setTimeout(() => this._resume(), 3000);
      } else if (!this._isHovered) {
        // Desktop: mouse alanda değilse hemen oyna
        this._resume();
      }
      // Desktop + mouse alanda: mouseleave tetikleyince oynar
    };

    this._viewport.addEventListener("pointerdown", onDown);
    this._viewport.addEventListener("pointermove", onMove);
    this._viewport.addEventListener("pointerup", onEnd);
    this._viewport.addEventListener("pointercancel", onEnd);

    // Görsel sürüklemeleri engelle
    this._viewport.addEventListener("dragstart", (e) => e.preventDefault());
  }
}

customElements.define("gagoz-collection", GagozCollection);
